/**
 * Devanagari birth-place resolution helpers.
 *
 * `devanagariToGazetteerQuery` is the ONE entry point both the autocomplete
 * (dropdown while typing) and the submit-time typed-city resolver call, so the
 * two surfaces can never disagree about what "मुंबई" means (rules 23/24 — no
 * drifting copies). The mapping table itself is policy-as-data in
 * `place-devanagari-data.ts`; see that file for the accuracy discipline.
 *
 * Non-Devanagari input passes through UNCHANGED, so English-path behavior is
 * byte-identical to before this module existed.
 */

import { DEVANAGARI_CITY_ENTRIES } from "./place-devanagari-data";

const DEVANAGARI_RANGE = /[ऀ-ॿ]/;

/** True when the value contains at least one Devanagari codepoint. */
export function hasDevanagari(value: string): boolean {
  return DEVANAGARI_RANGE.test(value);
}

/**
 * Canonical lookup key for a Devanagari city spelling: NFD-decompose, strip the
 * nukta (U+093C) and zero-width joiners, recompose, collapse whitespace. This
 * folds the common keyboard variants (गाजियाबाद/गाज़ियाबाद, फ/फ़) onto one entry.
 * @internal
 */
export function normalizeDevanagariKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[़‌‍]/g, "")
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

const DEVANAGARI_CITY_MAP: ReadonlyMap<string, string> = new Map(
  DEVANAGARI_CITY_ENTRIES.map(([devanagari, canonical]) => [
    normalizeDevanagariKey(devanagari),
    canonical,
  ])
);

function lookupDevanagariCity(value: string): string | null {
  return DEVANAGARI_CITY_MAP.get(normalizeDevanagariKey(value)) ?? null;
}

/**
 * Resolve a typed query to the gazetteer-searchable form.
 *
 * - Non-Devanagari input → returned unchanged (English path untouched).
 * - A known Devanagari city (exact table hit, full string or the part before a
 *   comma — "मुंबई, महाराष्ट्र" → "Mumbai") → the canonical English name.
 * - Unknown Devanagari → returned unchanged, so the search finds nothing and
 *   the caller surfaces the conservative "confirm your city" path. We NEVER
 *   fuzzy-guess a birth place (chart accuracy > completion, rule 29).
 */
export function devanagariToGazetteerQuery(query: string): string {
  const q = query.trim();
  if (!q || !hasDevanagari(q)) return query;

  const full = lookupDevanagariCity(q);
  if (full) return full;

  const beforeComma = q.split(",")[0] ?? "";
  const cityPart = lookupDevanagariCity(beforeComma);
  if (cityPart) return cityPart;

  return query;
}
